// PaymentTable.jsx

import React, { useState, useEffect, useMemo } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { computeAutoAdditionsFromHours, computeFeeTotals, computeMonthlyHourSummary, computeTotal2 } from '../utils/obracunCalculator';
import { getPersonEarningsForMonth, sumEarningsByWeek } from '../utils/obracunPersonContext';
import { getMonthlyFinancial, getMonthlyScalar } from '../utils/userPeriodDataUtils';

function PaymentTable({
  people = [],
  earningsData = [],
  userPeriodData = {},
  selectedYear,
  selectedMonth,
  selectedFirm,
  firms = [],
  dailyMetricsData = [],
  monthlyNightHoursMap = {},
  holidaysByDate = {},
  obracunRates = { sunday: 0, holiday: 0, overtime: 0, night: 0 },
}) {
  const [paymentData, setPaymentData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('paymentTableColumns');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    localStorage.setItem('paymentTableColumns', JSON.stringify(hiddenColumns));
  }, [hiddenColumns]);

  const getFirmName = (firmId) => {
    const firm = firms.find((f) => f.id === firmId);
    return firm ? firm.name : 'Sve';
  };

  const filteredPeople = useMemo(() => {
    return people
      .filter((person) => {
        const firmMatch = selectedFirm === '' || person.firmId === selectedFirm;
        const dateMatch =
          (person.startYear < selectedYear ||
            (person.startYear === selectedYear && person.startMonth <= selectedMonth)) &&
          (!person.archived ||
            person.archiveYear > selectedYear ||
            (person.archiveYear === selectedYear && person.archiveMonth > selectedMonth));
        const notArchived = !person.archived;
        return firmMatch && dateMatch && notArchived;
      })
      .sort((a, b) => {
        if (selectedFirm === '') {
          const firmA = firms.find((firm) => firm.id === a.firmId)?.name || '';
          const firmB = firms.find((firm) => firm.id === b.firmId)?.name || '';
          if (firmA === firmB) return a.id.localeCompare(b.id);
          return firmA.localeCompare(firmB);
        }
        return a.id.localeCompare(b.id);
      });
  }, [people, selectedFirm, selectedYear, selectedMonth, firms]);

  useEffect(() => {
    const data = filteredPeople.map((person) => {
      const monthlyDoc = userPeriodData[person.id];
      const personEarnings = getPersonEarningsForMonth(
        earningsData,
        person.id,
        selectedYear,
        selectedMonth
      );
      const { zaradaTotal } = sumEarningsByWeek(personEarnings);

      const fee = getMonthlyScalar(person, monthlyDoc, 'fee');
      const { total1 } = computeFeeTotals(zaradaTotal, fee);
      const tax = getMonthlyFinancial(person, monthlyDoc, 'tax');
      const rent = getMonthlyFinancial(person, monthlyDoc, 'rent');
      const petrol = getMonthlyFinancial(person, monthlyDoc, 'petrol');
      const acom = getMonthlyFinancial(person, monthlyDoc, 'acom');
      const utility = getMonthlyFinancial(person, monthlyDoc, 'utility');
      const sim = getMonthlyFinancial(person, monthlyDoc, 'sim');
      const advance = getMonthlyFinancial(person, monthlyDoc, 'advance');
      const bonus = getMonthlyScalar(person, monthlyDoc, 'bonus');
      const ostalo = getMonthlyScalar(person, monthlyDoc, 'ostalo');

      const hoursSummary = computeMonthlyHourSummary(
        person.id,
        selectedYear,
        selectedMonth,
        dailyMetricsData,
        holidaysByDate,
        monthlyNightHoursMap
      );
      const autoAmounts = computeAutoAdditionsFromHours(hoursSummary, obracunRates);

      const total2 = computeTotal2({
        total1,
        tax,
        rent,
        petrol,
        acom,
        utility,
        sim,
        advance,
        sunday: autoAmounts.sunday,
        holiday: autoAmounts.holiday,
        overtime: autoAmounts.overtime,
        night: autoAmounts.night,
        bonus,
        ostalo,
      });

      return {
        name: person.name,
        totalForPayment: parseFloat(total2.toFixed(2)),
        tekuciRacun: person.tekuciRacun || '',
        zasticeniRacun: person.zasticeniRacun || '',
        iban: person.iban || '',
        napomena: monthlyDoc?.napomena || '',
      };
    });

    setPaymentData(data);
  }, [
    filteredPeople,
    earningsData,
    userPeriodData,
    selectedYear,
    selectedMonth,
    dailyMetricsData,
    monthlyNightHoursMap,
    holidaysByDate,
    obracunRates,
  ]);

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Konačna isplata');

    worksheet.columns = [
      { header: 'Ime', key: 'name', width: 25 },
      { header: 'Konačna isplata', key: 'totalForPayment', width: 18 },
      { header: 'Tekući račun', key: 'tekuciRacun', width: 22 },
      { header: 'Zaštićeni račun', key: 'zasticeniRacun', width: 22 },
      { header: 'IBAN', key: 'iban', width: 28 },
      { header: 'Napomena', key: 'napomena', width: 30 },
    ];

    paymentData.forEach((row) => worksheet.addRow(row));

    worksheet.getRow(1).font = { bold: true };

    const firmNameForFile = selectedFirm
      ? getFirmName(selectedFirm).replace(/[^a-z0-9]/gi, '_')
      : 'sve';

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], { type: 'application/octet-stream' }),
      `Konacna_isplata_${firmNameForFile}_${selectedMonth}_${selectedYear}.xlsx`
    );
  };

  return (
    <div className="payment-table-container">
      <h2>Konačna isplata</h2>
      <button onClick={exportToExcel} className="export-button">
        Izvezi u Excel
      </button>
      <table>
        <thead>
          <tr>
            <th>Ime</th>
            <th>Total za isplatu</th>
            <th>Tekući račun</th>
            <th>Zaštićeni račun</th>
            <th>IBAN</th>
            <th>Napomene</th>
          </tr>
        </thead>
        <tbody>
          {paymentData.map((item, index) => (
            <tr key={index}>
              <td><strong>{item.name}</strong></td>
              <td>{item.totalForPayment.toFixed(2)}</td>
              <td>{item.tekuciRacun}</td>
              <td>{item.zasticeniRacun}</td>
              <td>{item.iban}</td>
              <td>{item.napomena}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PaymentTable;
